import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPlayerBookingApi, getFacilityRulesApi, sendOtpApi, verifyOtpApi, uploadPaymentProofApi, submitReceiptTextApi, startProofUploadApi, getBookingApi } from '../lib/api';
import { formatHour, formatTimeRange, formatDate, formatPrice, formatDuration } from '../lib/format';
import { ArrowLeft, MapPin, Clock, Calendar, Check, ChevronDown, ChevronUp, Mail, ShieldCheck, Upload, AlertTriangle, Image, XCircle, Copy, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const HOLD_MINUTES = 30; // Default hold time - actual value comes from facility rules
const GRACE_PERIOD_MINUTES = 2; // Extra time if upload started before expiry

function CountdownTimer({ expiresAt, onExpire, uploadStarted }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [expired, setExpired] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [inGracePeriod, setInGracePeriod] = useState(false);

  useEffect(() => {
    const update = () => {
      const now = Date.now();
      const diff = expiresAt - now;
      const graceDiff = expiresAt + (GRACE_PERIOD_MINUTES * 60 * 1000) - now;
      
      // Check if in grace period (past expiry but within grace window AND upload started)
      if (diff <= 0 && graceDiff > 0 && uploadStarted) {
        setInGracePeriod(true);
        const graceMins = Math.floor(graceDiff / 60000);
        const graceSecs = Math.floor((graceDiff % 60000) / 1000);
        setTimeLeft(`${graceMins}m ${graceSecs}s`);
        setUrgent(true);
        return;
      }
      
      if (diff <= 0) {
        setTimeLeft('Expired');
        setExpired(true);
        setUrgent(true);
        setInGracePeriod(false);
        if (onExpire) onExpire();
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}m ${secs}s`);
      setUrgent(diff < 300000); // urgent under 5 minutes
      setInGracePeriod(false);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, onExpire, uploadStarted]);

  if (expired) {
    return (
      <div
        className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-medium bg-status-expired-bg text-status-expired border border-status-expired/20"
        data-testid="expiration-timer"
      >
        <XCircle className="w-4 h-4 shrink-0" />
        <span>This hold has expired. The court has been released.</span>
      </div>
    );
  }

  if (inGracePeriod) {
    return (
      <div
        className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-medium bg-amber-50 text-amber-800 border border-amber-300"
        data-testid="grace-period-timer"
      >
        <Upload className="w-4 h-4 shrink-0 animate-pulse" />
        <span>Finishing upload... {timeLeft} remaining</span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-medium ${
        urgent
          ? 'bg-status-expired-bg text-status-expired border border-status-expired/20'
          : 'bg-status-held-bg text-status-held border border-status-held/30'
      }`}
      data-testid="expiration-timer"
    >
      {urgent ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <Clock className="w-4 h-4 shrink-0" />}
      <span>Held for {timeLeft}</span>
    </div>
  );
}

export default function PlayerConfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const bookingData = location.state;
  const isBallMachine = bookingData?.booking_intent === 'ball_machine';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [rules, setRules] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [conflictSuggestion, setConflictSuggestion] = useState(null);
  const [equipmentSelected, setEquipmentSelected] = useState(isBallMachine);

  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue, setOtpValue] = useState(['', '', '', '', '', '']);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [verifyToken, setVerifyToken] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [memberName, setMemberName] = useState(null);
  const otpRefs = useRef([]);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [proofUploaded, setProofUploaded] = useState(false);
  const [proofType, setProofType] = useState(null); // 'screenshot' or 'receipt_text'
  const [previewUrl, setPreviewUrl] = useState(null);
  const [noCourtConflict, setNoCourtConflict] = useState(null);
  const [uploadStarted, setUploadStarted] = useState(false);
  const fileInputRef = useRef(null);
  
  // Receipt text state
  const [receiptText, setReceiptText] = useState('');
  const [parsedReceipt, setParsedReceipt] = useState(null);
  const [submittingReceipt, setSubmittingReceipt] = useState(false);
  
  // Expiration state
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!bookingData) {
      navigate('/book');
      return;
    }
    loadRules();
  }, []);
  
  // Poll booking status to detect server-side expiration or release
  useEffect(() => {
    if (!confirmed || !bookingResult || isExpired || proofUploaded) return;
    
    const pollStatus = async () => {
      try {
        const res = await getBookingApi(bookingResult.id);
        const status = res.data.status;
        if (status === 'expired' || status === 'released' || status === 'cancelled') {
          setIsExpired(true);
          toast.error('This reservation has expired.');
        }
      } catch (err) {
        // Booking might be deleted
        if (err.response?.status === 404) {
          setIsExpired(true);
        }
      }
    };
    
    // Poll every 5 seconds
    const interval = setInterval(pollStatus, 5000);
    return () => clearInterval(interval);
  }, [confirmed, bookingResult, isExpired, proofUploaded]);

  const handleExpire = useCallback(() => {
    setIsExpired(true);
  }, []);

  const loadRules = async () => {
    try {
      const res = await getFacilityRulesApi();
      setRules(res.data);
    } catch (err) {
      console.error('Failed to load rules:', err);
    }
  };

  if (!bookingData) return null;

  const startHour = bookingData.start_hour;
  const endHour = startHour + bookingData.duration_hours;
  const dayRate = isBallMachine ? (rules?.training_day_rate ?? 250) : (rules?.day_rate ?? 350);
  const eveningRate = rules?.evening_rate ?? (rules?.hourly_rate || 350);
  const regularEveningStart = rules?.evening_start_hour ?? 16;
  const trainingEveningStart = rules?.training_evening_start_hour ?? 18;
  // Training Package bookings use a later evening cutoff (day rate holds until 5-6 PM)
  const eveningStart = isBallMachine ? trainingEveningStart : regularEveningStart;
  const equipmentRatePlayer = rules?.equipment_rate_player ?? 450;

  let dayHours = 0, eveningHours = 0;
  for (let h = startHour; h < endHour; h++) {
    if (h < eveningStart) dayHours += 1; else eveningHours += 1;
  }
  const courtCost = dayHours * dayRate + eveningHours * eveningRate;
  const memberTrainingRate = rules?.member_training_rate ?? 400;
  const effectiveEquipmentRate = isMember ? memberTrainingRate : equipmentRatePlayer;
  const equipmentCost = equipmentSelected ? effectiveEquipmentRate * bookingData.duration_hours : 0;
  const totalAmount = courtCost + equipmentCost;
  const hourlyRate = eveningRate; // legacy label fallback
  const expireMinutes = rules?.auto_expire_minutes || 30;
  const gcashNumber = rules?.gcash_number || '09XX XXX XXXX';
  const gcashAccountName = rules?.gcash_account_name || 'TP Pickleball';
  const gcashQrImage = rules?.gcash_qr_image || '';

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSendOtp = async () => {
    if (!isValidEmail(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    setSendingOtp(true);
    try {
      const res = await sendOtpApi(email);
      setOtpSent(true);
      toast.success('Verification code sent to your email', { duration: 5000 });
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      toast.error('Failed to send verification code');
    }
    setSendingOtp(false);
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpValue];
    newOtp[index] = value;
    setOtpValue(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
    if (value && index === 5) {
      const fullOtp = newOtp.join('');
      if (fullOtp.length === 6) handleVerifyOtp(fullOtp);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValue[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setOtpValue(paste.split(''));
      otpRefs.current[5]?.focus();
      handleVerifyOtp(paste);
      e.preventDefault();
    }
  };

  const handleVerifyOtp = async (code) => {
    setVerifyingOtp(true);
    try {
      const res = await verifyOtpApi(email, code);
      setVerifyToken(res.data.verify_token);
      setIsMember(!!res.data.is_member);
      setMemberName(res.data.member_name || null);
      setOtpVerified(true);
      toast.success(res.data.is_member ? 'Welcome back, Turning Point Member' : 'Email verified');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
      setOtpValue(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    }
    setVerifyingOtp(false);
  };

  const handleBooking = async () => {
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (!otpVerified) { toast.error('Please verify your email first'); return; }
    setSubmitting(true);
    setConflictSuggestion(null);
    setNoCourtConflict(null);
    try {
      const res = await createPlayerBookingApi({
        court_id: bookingData.court_id,
        date: bookingData.date,
        start_hour: bookingData.start_hour,
        duration_hours: bookingData.duration_hours,
        player_name: name.trim(),
        player_email: email.trim(),
        player_phone: phone.trim() || null,
        verify_token: verifyToken,
        has_equipment: equipmentSelected,
        booking_intent: isBallMachine ? 'ball_machine' : 'court',
      });
      setBookingResult(res.data);
      setConfirmed(true);
      toast.success("Slot reserved! Send your GCash payment now.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        if (detail.type === 'no_courts') {
          setNoCourtConflict(detail);
        } else if (detail.alternative_court_id) {
          setConflictSuggestion(detail);
        } else {
          toast.error(detail.message || 'Booking failed');
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Booking failed');
      }
    }
    setSubmitting(false);
  };

  const handleSwitchCourt = async () => {
    if (!conflictSuggestion) return;
    setSubmitting(true);
    try {
      const res = await createPlayerBookingApi({
        court_id: conflictSuggestion.alternative_court_id,
        date: bookingData.date,
        start_hour: bookingData.start_hour,
        duration_hours: bookingData.duration_hours,
        player_name: name.trim(),
        player_email: email.trim(),
        player_phone: phone.trim() || null,
        verify_token: verifyToken,
        has_equipment: equipmentSelected,
        booking_intent: isBallMachine ? 'ball_machine' : 'court',
      });
      setBookingResult(res.data);
      setConfirmed(true);
      setConflictSuggestion(null);
      toast.success("Slot reserved! Send your GCash payment now.");
    } catch (err) {
      toast.error('Booking failed. Try another time.');
    }
    setSubmitting(false);
  };

  const handleBookClosestSlot = async (slot) => {
    setSubmitting(true);
    try {
      const res = await createPlayerBookingApi({
        court_id: slot.court_id,
        date: bookingData.date,
        start_hour: slot.start_hour,
        duration_hours: slot.end_hour - slot.start_hour,
        player_name: name.trim(),
        player_email: email.trim(),
        player_phone: phone.trim() || null,
        verify_token: verifyToken,
        has_equipment: equipmentSelected,
        booking_intent: isBallMachine ? 'ball_machine' : 'court',
      });
      setBookingResult(res.data);
      setConfirmed(true);
      setNoCourtConflict(null);
      toast.success("Slot reserved! Send your GCash payment now.");
    } catch (err) {
      toast.error('That slot was just taken too. Try another.');
    }
    setSubmitting(false);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !bookingResult) return;
    
    // Don't allow upload if expired (and we haven't started upload yet)
    if (isExpired && !uploadStarted) {
      toast.error('This reservation has expired. Please book again.');
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    
    try {
      // Signal to backend that upload has started (for grace period)
      if (!uploadStarted) {
        try {
          await startProofUploadApi(bookingResult.id);
          setUploadStarted(true);
        } catch (startErr) {
          // Handle 409 - booking already expired before we could start
          if (startErr.response?.status === 409) {
            setIsExpired(true);
            toast.error(startErr.response?.data?.detail || 'This reservation has expired. Please book again.');
            setPreviewUrl(null);
            setUploading(false);
            return;
          }
          // For other errors, continue with upload (best effort)
          console.warn('Could not signal upload start:', startErr);
        }
      }
      
      await uploadPaymentProofApi(bookingResult.id, file);
      setProofUploaded(true);
      toast.success('Payment proof sent! Awaiting verification.');
      // Navigate to booking status page after brief delay
      setTimeout(() => {
        navigate(`/booking/${bookingResult.id}`);
      }, 1500);
    } catch (err) {
      // Handle 409 - booking expired
      if (err.response?.status === 409) {
        setIsExpired(true);
        toast.error(err.response?.data?.detail || 'This reservation has expired. Please book again.');
      } else {
        toast.error('Upload failed. Try again.');
      }
      setPreviewUrl(null);
    }
    setUploading(false);
  };

  // Parse receipt text locally for preview
  const parseReceiptLocally = (text) => {
    if (!text.trim()) return null;
    const parsed = {};
    const amtMatch = text.match(/₱([\d,]+\.?\d*)/);
    if (amtMatch) parsed.amount = amtMatch[1];
    const refMatch = text.match(/Ref\.?\s*No\.?\s*(\d+)/i);
    if (refMatch) parsed.reference = refMatch[1];
    const tsMatch = text.match(/([A-Z][a-z]{2}\s+\d{1,2},?\s+\d{4}\s+\d{1,2}:\d{2}\s*[AP]M)/i);
    if (tsMatch) parsed.timestamp = tsMatch[1];
    return (parsed.amount || parsed.reference) ? parsed : null;
  };

  const handleReceiptTextChange = (text) => {
    setReceiptText(text);
    setParsedReceipt(parseReceiptLocally(text));
  };

  const handleSubmitReceipt = async () => {
    if (!receiptText.trim() || !bookingResult) return;
    if (isExpired && !uploadStarted) {
      toast.error('This reservation has expired. Please book again.');
      return;
    }
    setSubmittingReceipt(true);
    try {
      if (!uploadStarted) {
        try {
          await startProofUploadApi(bookingResult.id);
          setUploadStarted(true);
        } catch (startErr) {
          if (startErr.response?.status === 409) {
            setIsExpired(true);
            toast.error(startErr.response?.data?.detail || 'This reservation has expired.');
            setSubmittingReceipt(false);
            return;
          }
        }
      }
      await submitReceiptTextApi(bookingResult.id, receiptText);
      setProofUploaded(true);
      setProofType('receipt_text');
      toast.success('Receipt submitted! Awaiting verification.');
      setTimeout(() => navigate(`/booking/${bookingResult.id}`), 1500);
    } catch (err) {
      if (err.response?.status === 409) {
        setIsExpired(true);
        toast.error(err.response?.data?.detail || 'This reservation has expired.');
      } else {
        toast.error('Submission failed. Try again.');
      }
    }
    setSubmittingReceipt(false);
  };

  // Confirmed state — show payment instructions + upload
  if (confirmed && bookingResult) {
    // Use server's hold_expires_at if available, otherwise calculate from created_at
    const expiresAt = bookingResult.hold_expires_at 
      ? new Date(bookingResult.hold_expires_at).getTime()
      : new Date(bookingResult.created_at).getTime() + HOLD_MINUTES * 60 * 1000;
    const courtName = bookingResult.court_name || bookingData.court_name;
    
    // Show expired UI
    if (isExpired) {
      return (
        <div className="min-h-screen bg-app-bg" data-testid="booking-expired">
          <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-10">
            <div className="max-w-lg mx-auto flex items-center gap-3">
              <h1 className="font-heading text-lg font-semibold text-gray-900">Reservation Expired</h1>
            </div>
          </div>

          <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
            {/* Expired Status */}
            <div className="text-center animate-slide-up">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-status-expired-bg">
                <XCircle className="w-7 h-7 text-status-expired" />
              </div>
              <h2 className="font-heading text-xl font-bold text-gray-900 mb-1">
                Reservation Expired
              </h2>
              <p className="text-sm text-gray-500">
                The {HOLD_MINUTES}-minute payment window has passed and this slot has been released.
              </p>
            </div>

            {/* Expired Booking Details */}
            <div className="bg-gray-50 rounded-3xl border border-gray-200 p-5 opacity-60" data-testid="expired-booking-details">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500 line-through">{formatDate(bookingResult.date)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500 line-through">{formatTimeRange(bookingResult.start_hour, bookingResult.end_hour)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500 line-through">{courtName}</span>
                </div>
              </div>
            </div>

            {/* CTA to book again */}
            <Button
              onClick={() => navigate('/book')}
              className="w-full h-14 rounded-2xl bg-brand-teal hover:bg-brand-teal-hover text-white font-semibold"
              data-testid="pick-new-time-btn"
            >
              Pick a New Time
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-app-bg" data-testid="booking-confirmed">
        <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-10">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <h1 className="font-heading text-lg font-semibold text-gray-900">Court held for you ⏳</h1>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
          {/* Status */}
          <div className="text-center animate-slide-up">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${proofUploaded ? 'bg-brand-teal' : 'bg-status-held'}`}>
              {proofUploaded ? <Check className="w-7 h-7 text-white" /> : <Clock className="w-7 h-7 text-white" />}
            </div>
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-1">
              {proofUploaded ? 'Payment proof sent!' : 'Court held for you'}
            </h2>
            <p className="text-sm text-gray-500">
              {proofUploaded
                ? 'You\'ll see confirmation after verification'
                : 'Send your GCash payment and upload the screenshot below'}
            </p>
          </div>

          {/* Expiration Warning */}
          {!proofUploaded && (
            <CountdownTimer expiresAt={expiresAt} onExpire={handleExpire} uploadStarted={uploadStarted} />
          )}

          {/* Booking Details */}
          <div className="bg-white rounded-3xl border border-black/5 p-5" data-testid="confirmed-booking-details">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{formatDate(bookingResult.date)}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{formatTimeRange(bookingResult.start_hour, bookingResult.end_hour)}</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{courtName}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{email}</span>
              </div>
            </div>
            <div className="border-t border-gray-100 mt-4 pt-4 flex justify-between">
              <span className="text-sm text-gray-500">Amount due</span>
              <span className="font-heading font-bold text-gray-900">{formatPrice(bookingResult.amount)}</span>
            </div>
          </div>

          {/* GCash Payment Instructions */}
          {!proofUploaded && (
            <div className="bg-brand-teal-tint rounded-3xl border border-brand-teal/20 p-5 space-y-3" data-testid="gcash-instructions">
              <p className="text-sm font-semibold text-brand-teal">Send via GCash</p>
              <div className="bg-white rounded-xl p-3 space-y-1">
                <p className="text-xs text-gray-400">GCash Number</p>
                <div className="flex items-center justify-between">
                  <p className="text-base font-semibold text-gray-900 font-mono">{gcashNumber}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(gcashNumber.replace(/\s/g, ''));
                      toast.success('GCash number copied!');
                    }}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-brand-teal/30 hover:bg-brand-teal-tint transition-colors text-xs font-medium text-brand-teal"
                    data-testid="copy-gcash-number-btn"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                </div>
              </div>
              <div className="bg-white rounded-xl p-3 space-y-1">
                <p className="text-xs text-gray-400">Account Name</p>
                <p className="text-base font-semibold text-gray-900">{gcashAccountName}</p>
              </div>
              <div className="bg-white rounded-xl p-3 space-y-1">
                <p className="text-xs text-gray-400">Amount</p>
                <p className="text-base font-semibold text-gray-900">{formatPrice(bookingResult.amount)}</p>
              </div>
              {gcashQrImage && (
                <div className="flex justify-center pt-1">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${gcashQrImage}`}
                    alt="GCash QR Code"
                    className="rounded-xl border border-brand-teal/20 max-w-[180px] w-full"
                    data-testid="gcash-qr-code"
                  />
                </div>
              )}
              <p className="text-xs text-brand-teal">After sending, upload your screenshot or paste receipt below</p>
            </div>
          )}

          {/* Hybrid Payment Proof Submission */}
          {!proofUploaded ? (
            <div data-testid="upload-proof-section" className="space-y-4">
              {/* Option 1: Upload Screenshot */}
              <div className="bg-white rounded-3xl border border-black/5 p-5">
                <p className="text-sm font-semibold text-gray-800 mb-3">Upload Payment Screenshot</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="proof-file-input"
                />
                {previewUrl && (
                  <div className="mb-3 rounded-2xl overflow-hidden border border-black/5">
                    <img src={previewUrl} alt="Payment proof" className="w-full max-h-48 object-contain bg-gray-50" />
                  </div>
                )}
                <Button
                  data-testid="upload-proof-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || submittingReceipt}
                  className="w-full bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full py-5 text-sm font-semibold shadow-sm transition-all active:scale-[0.98]"
                >
                  {uploading ? 'Uploading...' : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload GCash Screenshot
                    </>
                  )}
                </Button>
              </div>

              {/* OR Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Option 2: Paste GCash Receipt */}
              <div className="bg-white rounded-3xl border border-black/5 p-5">
                <p className="text-sm font-semibold text-gray-800 mb-1">Paste GCash Receipt</p>
                <p className="text-xs text-gray-400 mb-3">After sending payment, tap "Share Receipt" in GCash and paste the receipt text below.</p>
                <textarea
                  data-testid="receipt-text-input"
                  value={receiptText}
                  onChange={(e) => handleReceiptTextChange(e.target.value)}
                  placeholder={"You sent ₱700.00\nto 0917 304 3229\nMar 07, 2026 1:17 PM\nRef No. 7038508458312"}
                  className="w-full rounded-xl border border-gray-200 p-3 text-sm text-gray-700 placeholder:text-gray-300 resize-none focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal"
                  rows={5}
                />
                {parsedReceipt && (
                  <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1" data-testid="parsed-receipt-preview">
                    <p className="text-xs font-semibold text-emerald-800">Detected payment:</p>
                    {parsedReceipt.amount && <p className="text-xs text-emerald-700">Amount: ₱{parsedReceipt.amount}</p>}
                    {parsedReceipt.timestamp && <p className="text-xs text-emerald-700">Time: {parsedReceipt.timestamp}</p>}
                    {parsedReceipt.reference && <p className="text-xs text-emerald-700">Reference: {parsedReceipt.reference}</p>}
                  </div>
                )}
                <Button
                  data-testid="submit-receipt-btn"
                  onClick={handleSubmitReceipt}
                  disabled={!receiptText.trim() || submittingReceipt || uploading}
                  className="w-full mt-3 bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full py-5 text-sm font-semibold shadow-sm transition-all active:scale-[0.98]"
                >
                  {submittingReceipt ? 'Submitting...' : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Submit Receipt
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-center text-gray-400">
                You'll see confirmation after verification
              </p>
              <button
                data-testid="pre-upload-status-link"
                onClick={() => navigate(`/booking/${bookingResult.id}`)}
                className="w-full text-center text-xs text-gray-400 hover:text-brand-teal transition-colors py-2"
              >
                Check booking status anytime
              </button>
            </div>
          ) : (
            <div className="space-y-3 animate-slide-up">
              {previewUrl && (
                <div className="rounded-2xl overflow-hidden border border-brand-teal/20">
                  <img src={previewUrl} alt="Payment proof" className="w-full max-h-48 object-contain bg-gray-50" />
                  <div className="flex items-center gap-2 p-3 bg-brand-teal-tint">
                    <Check className="w-4 h-4 text-brand-teal" />
                    <span className="text-sm font-medium text-brand-teal">Screenshot uploaded</span>
                  </div>
                </div>
              )}
              {proofType === 'receipt_text' && parsedReceipt && (
                <div className="rounded-2xl overflow-hidden border border-brand-teal/20 p-4 bg-brand-teal-tint">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-4 h-4 text-brand-teal" />
                    <span className="text-sm font-medium text-brand-teal">Receipt submitted</span>
                  </div>
                  {parsedReceipt.amount && <p className="text-xs text-brand-teal">Amount: ₱{parsedReceipt.amount}</p>}
                  {parsedReceipt.reference && <p className="text-xs text-brand-teal">Ref: {parsedReceipt.reference}</p>}
                </div>
              )}
              <p className="text-sm text-center text-gray-500">
                Awaiting verification. You can check your booking status anytime.
              </p>
              <Button
                data-testid="view-booking-status-btn"
                onClick={() => navigate(`/booking/${bookingResult.id}`)}
                className="w-full bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full py-5 font-semibold"
              >
                View Booking Status
              </Button>
              <Button
                data-testid="back-to-home-btn"
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full rounded-full py-5 border-gray-200 text-gray-600"
              >
                Back to Home
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Pre-booking form
  return (
    <div className="min-h-screen bg-app-bg" data-testid="player-confirm">
      {/* Header */}
      <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            data-testid="confirm-back-btn"
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="font-heading text-lg font-semibold text-gray-900">Confirm booking</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Ball machine intent pill */}
        {isBallMachine && (
          <div
            data-testid="confirm-ball-machine-pill"
            className="flex items-center gap-3 px-4 py-3 rounded-2xl border"
            style={{ backgroundColor: '#F7FBEA', borderColor: '#D9E6B5' }}
          >
            <span className="w-7 h-7 rounded-md overflow-hidden shrink-0 flex items-center justify-center" style={{ backgroundColor: '#EEF6C6' }}><img src="/icons/training.png" alt="" className="w-full h-full object-cover" /></span>
            <div className="flex-1 leading-tight">
              <p className="text-sm font-semibold" style={{ color: '#6F8C00' }}>Training Session</p>
              <p className="text-xs mt-0.5" style={{ color: '#4B5563' }}>Everything you need for focused practice.</p>
            </div>
          </div>
        )}

        {/* Booking Summary */}
        <div className="bg-white rounded-3xl border border-black/5 p-5" data-testid="booking-summary">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="font-heading text-xl font-bold text-gray-900">
                {formatTimeRange(bookingData.start_hour, bookingData.end_hour)}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">
                {formatDate(bookingData.date)} · {formatDuration(bookingData.duration_hours)}
              </p>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-teal-tint">
              <MapPin className="w-3.5 h-3.5 text-brand-teal" />
              <span className="text-sm font-medium text-brand-teal">{bookingData.court_name}</span>
            </div>
          </div>

          {/* Price */}
          <div className="border-t border-gray-100 pt-4 mt-4">
            {isBallMachine ? (
              /* Training: always-expanded breakdown, no toggle */
              <div data-testid="price-breakdown" className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-1">Price Breakdown</p>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Court Rental</span>
                  <span>{formatPrice(courtCost)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Training Package{isMember ? ' (Member)' : ''}</span>
                  <span>
                    {isMember && (
                      <span className="text-gray-400 line-through mr-1.5">{formatPrice(equipmentRatePlayer * bookingData.duration_hours)}</span>
                    )}
                    {formatPrice(equipmentCost)}
                  </span>
                </div>
                <div className="border-t border-gray-100 pt-2 flex justify-between items-center">
                  <span className="font-heading text-sm font-semibold text-gray-900">Total</span>
                  <span className="font-heading text-lg font-bold text-gray-900">{formatPrice(totalAmount)}</span>
                </div>
                {isMember && (
                  <div
                    data-testid="member-rate-applied"
                    className="flex items-center gap-1.5 mt-1 text-xs font-semibold"
                    style={{ color: '#6F8C00' }}
                  >
                    <Check className="w-3.5 h-3.5" />
                    Turning Point Member Rate Applied
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2 leading-snug">We'll have your court, ball machine and equipment ready for your session.</p>
              </div>
            ) : (
              /* Regular court: collapsible breakdown */
              <>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-heading text-lg font-bold text-gray-900">{formatPrice(totalAmount)}</span>
                  <button
                    data-testid="toggle-breakdown-btn"
                    onClick={() => setShowBreakdown(!showBreakdown)}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                  >
                    {showBreakdown ? 'Hide' : 'Show'} breakdown
                    {showBreakdown ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                </div>
                {showBreakdown && (
                  <div className="space-y-1.5 text-sm animate-fade-in" data-testid="price-breakdown">
                    {dayHours > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span>{formatPrice(dayRate)} × {dayHours} day-hr{dayHours > 1 ? 's' : ''}</span>
                        <span>{formatPrice(dayRate * dayHours)}</span>
                      </div>
                    )}
                    {eveningHours > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span>{formatPrice(eveningRate)} × {eveningHours} evening-hr{eveningHours > 1 ? 's' : ''}</span>
                        <span>{formatPrice(eveningRate * eveningHours)}</span>
                      </div>
                    )}
                    {equipmentSelected && (
                      <div className="flex justify-between text-gray-500">
                        <span>{formatPrice(equipmentRatePlayer)} × {bookingData.duration_hours} equip-hr{bookingData.duration_hours > 1 ? 's' : ''}</span>
                        <span>{formatPrice(equipmentCost)}</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Conflict Suggestion — alternative court */}
        {conflictSuggestion && (
          <div className="bg-status-held-bg rounded-2xl border border-status-held/30 p-4 animate-slide-up" data-testid="conflict-suggestion">
            <p className="text-sm text-status-held mb-3">{conflictSuggestion.message}</p>
            <p className="text-sm font-medium text-status-held mb-3">
              {conflictSuggestion.suggestion} — {conflictSuggestion.alternative_court_name}
            </p>
            <Button
              data-testid="switch-court-btn"
              onClick={handleSwitchCourt}
              disabled={submitting}
              className="bg-status-held hover:opacity-90 text-white rounded-full px-6 py-2 text-sm"
            >
              Switch to {conflictSuggestion.alternative_court_name}
            </Button>
          </div>
        )}

        {/* Conflict — no courts available, show closest times */}
        {noCourtConflict && (
          <div className="bg-white rounded-3xl border border-black/5 p-5 animate-slide-up" data-testid="no-court-conflict">
            <p className="font-heading text-lg font-bold text-gray-900 mb-1">{noCourtConflict.headline}</p>
            <p className="text-sm text-gray-500 mb-5 whitespace-pre-line">{noCourtConflict.message}</p>

            <div className="space-y-2">
              {(noCourtConflict.closest_slots || []).map((slot, i) => (
                <button
                  key={i}
                  data-testid={`closest-slot-${i}`}
                  onClick={() => handleBookClosestSlot(slot)}
                  disabled={submitting}
                  className="w-full flex items-center justify-between p-3.5 rounded-2xl border border-gray-100 hover:border-brand-teal hover:bg-brand-teal-tint/30 transition-all text-left active:scale-[0.98]"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {slot.start_label}–{slot.end_label}
                    </p>
                    <p className="text-xs text-gray-400">{slot.court_name}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-300 -rotate-90" />
                </button>
              ))}
            </div>

            <button
              data-testid="back-to-times-btn"
              onClick={() => navigate('/book')}
              className="w-full text-center text-xs text-gray-400 hover:text-brand-teal transition-colors py-3 mt-2"
            >
              Browse all available times
            </button>
          </div>
        )}

        {/* Player Info */}
        <div className="bg-white rounded-3xl border border-black/5 p-5 space-y-4" data-testid="player-info-form">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Your details</p>

          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-sm text-gray-600 mb-1.5">Name</Label>
            <Input
              id="name"
              data-testid="player-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="rounded-xl bg-gray-50 border-transparent focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
            />
          </div>

          {/* Email + OTP */}
          <div>
            <Label htmlFor="email" className="text-sm text-gray-600 mb-1.5">Email</Label>
            <div className="flex gap-2">
              <Input
                id="email"
                data-testid="player-email-input"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setOtpSent(false); setOtpVerified(false); setOtpValue(['', '', '', '', '', '']); }}
                placeholder="you@email.com"
                disabled={otpVerified}
                className="rounded-xl bg-gray-50 border-transparent focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 flex-1"
              />
              {!otpVerified && (
                <Button
                  data-testid="send-otp-btn"
                  onClick={handleSendOtp}
                  disabled={sendingOtp || !isValidEmail(email)}
                  variant="outline"
                  className="rounded-xl px-4 text-sm border-brand-teal text-brand-teal hover:bg-brand-teal-tint shrink-0"
                >
                  {sendingOtp ? 'Sending...' : otpSent ? 'Resend' : 'Verify'}
                </Button>
              )}
              {otpVerified && (
                <div className="flex items-center gap-1.5 px-3 rounded-xl bg-brand-teal-tint text-brand-teal shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-sm font-medium">Verified</span>
                </div>
              )}
            </div>
            {!otpVerified && (
              <p className="text-xs text-gray-400 mt-1.5">Used to send your booking confirmation</p>
            )}
          </div>

          {/* OTP Input */}
          {otpSent && !otpVerified && (
            <div className="animate-slide-up" data-testid="otp-input-section">
              <Label className="text-sm text-gray-600 mb-2 block">Enter the 6-digit code</Label>
              <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                {otpValue.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    data-testid={`otp-digit-${i}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="w-11 h-12 text-center text-lg font-semibold rounded-xl bg-gray-50 border border-gray-200 focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 outline-none transition-all"
                  />
                ))}
              </div>
              {verifyingOtp && <p className="text-xs text-gray-400 text-center mt-2">Verifying...</p>}
              <p className="text-xs text-gray-400 text-center mt-2">Check the notification for your code</p>
            </div>
          )}

          {/* Phone */}
          <div>
            <Label htmlFor="phone" className="text-sm text-gray-600 mb-1.5">Phone <span className="text-gray-400">(optional)</span></Label>
            <Input
              id="phone"
              data-testid="player-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+63 9XX XXX XXXX"
              className="rounded-xl bg-gray-50 border-transparent focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
            />
          </div>
        </div>

        {/* Book Button */}
        <Button
          data-testid="confirm-booking-btn"
          onClick={handleBooking}
          disabled={submitting || !name.trim() || !otpVerified}
          className="w-full bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all active:scale-[0.98] disabled:opacity-50"
        >
          {submitting ? 'Confirming...' : 'Confirm Booking'}
        </Button>
        <p className="text-xs text-center text-gray-400">
          You'll have {HOLD_MINUTES} minutes to send GCash payment
        </p>
      </div>
    </div>
  );
}
